import { Box, styled, Typography } from '@mui/material';

import { ActiveCaseCardPreview, NewCaseCardPreview } from '#entities';

import { AirFleetCardPreview } from '../../AirFleetTracker';
import { CrewAvailabilityCardPreview } from '../../CrewAvailability';
import { GeoMapPreview } from '../../GeoMap';
import { HubsReportPreview } from '../../HubsReport';
import { StatisticsPreview } from '../../Statistics';
import { SystemMonitorPreview } from '../../SystemMonitor';
import { WindMapPreview } from '../../WindMap';

import type { WidgetType } from '../../widgetManager';
import type { ReactElement } from 'react';

const PreviewContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.utils.clamp(120, 250),
  height: theme.utils.clamp(150, 200),
  pointerEvents: 'none',
  userSelect: 'none',
}));

const TextPreview = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 'bold',
  textAlign: 'center',
  color: theme.palette.text.primary,
}));

/**
 * Maps widget type to its preview component
 * @param widgetType - The type of widget to preview
 * @param id - Optional widget id to distinguish between variants (e.g., 'geo-map-center' vs 'geo-map-focus')
 * @returns ReactElement representing the widget preview
 */
export const getWidgetPreview = (
  widgetType: WidgetType,
  id?: string,
): ReactElement => {
  switch (widgetType) {
    case 'new-cases':
      return <NewCaseCardPreview />;
    case 'active-cases':
      return <ActiveCaseCardPreview />;
    case 'airfleet-tracker':
      return <AirFleetCardPreview />;
    case 'crew-availability':
      return <CrewAvailabilityCardPreview />;
    case 'geo-map':
      return <GeoMapPreview isMainMap={id === 'geo-map-center'} />;
    case 'hubs-report':
      return <HubsReportPreview />;
    case 'statistics':
      return <StatisticsPreview />;
    case 'system-monitor':
      return <SystemMonitorPreview />;
    case 'wind-map':
      return <WindMapPreview />;
    default:
      return (
        <PreviewContainer>
          <TextPreview>{widgetType}</TextPreview>
        </PreviewContainer>
      );
  }
};

/**
 * Gets display name for a widget type
 * @param widgetType - The type of widget
 * @returns Human-readable display name
 */
export const getWidgetDisplayName = (widgetType: WidgetType): string => {
  const displayNameMap: Record<WidgetType, string> = {
    'new-cases': 'New Cases',
    'active-cases': 'Active Cases',
    'airfleet-tracker': 'Air Fleet Tracker',
    'crew-availability': 'Crew Availability',
    'geo-map': 'Geo Map',
    'hubs-report': 'Hubs Report',
    statistics: 'Statistics',
    'system-monitor': 'System Monitor',
    'wind-map': 'Wind Map',
  };

  return displayNameMap[widgetType] || widgetType;
};
